import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { keys, type Group, type LinkSet, type Link } from '../api/hierarchy';
import type { OfflinePrefetch, UserConfig, DeviceConfig } from '../api/config';
import { DEVICE_CONFIG_DEFAULTS } from '../api/config';
import { usePublicConfig } from '../api/public-config';

interface PrefetchState {
  running: boolean;
  done: boolean;
  completed: number;
  total: number;
  error: string | null;
}

/**
 * Pre-fills the offline cache so the user doesn't have to tour the app
 * first. Respects DeviceConfig.offlinePrefetch (OFF | SETS | FULL - null
 * means inherit DEVICE_CONFIG_DEFAULTS.offlinePrefetch which is FULL),
 * the admin global SystemSettings.pageCacheEnabled kill switch (off ⇒
 * prefetch forced to OFF on every device), and `Save-Data` hints so
 * metered connections aren't blasted with a dozen fetches on sign-in.
 *
 * Each call returns a { run } function for manual "Sync now" + a live
 * status (running / progress / error) for surfacing in the UI.
 */
export function usePrefetchAll(
  userId: string | null,
  dc: DeviceConfig | undefined,
): PrefetchState & { run: () => Promise<void> } {
  const qc = useQueryClient();
  const [state, setState] = useState<PrefetchState>({
    running: false,
    done: false,
    completed: 0,
    total: 0,
    error: null,
  });
  const ranForUserRef = useRef<string | null>(null);

  // Admin global kill switch overrides any per-device setting. If the
  // admin has flipped pageCacheEnabled off (something went wrong with
  // the offline-cache feature, blast radius mitigation), force OFF.
  const adminCacheOn = usePublicConfig((s) => s.config.pageCacheEnabled);
  const deviceMode = dc?.offlinePrefetch ?? DEVICE_CONFIG_DEFAULTS.offlinePrefetch;
  const mode: OfflinePrefetch = adminCacheOn ? deviceMode : 'OFF';

  async function run(): Promise<void> {
    if (!userId) return;
    setState({ running: true, done: false, completed: 0, total: 0, error: null });
    try {
      // Config + contacts list - cheap, always prefetch.
      await Promise.all([
        qc.prefetchQuery({
          queryKey: ['user-config'],
          queryFn: () => api.get('config').json<UserConfig>(),
        }),
        qc.prefetchQuery({
          queryKey: ['contacts', 'list'],
          queryFn: () => api.get('contacts').json<unknown>(),
        }),
      ]);

      if (mode === 'OFF') {
        setState((s) => ({ ...s, running: false, done: true, total: 2, completed: 2 }));
        return;
      }

      // Groups.
      const groups = await qc.fetchQuery({
        queryKey: keys.groups(),
        queryFn: () => api.get('groups').json<Group[]>(),
      });

      // Sets for every group - parallel, but bounded so we don't blast the API.
      const setsByGroup = await throttledMap(groups, 6, async (g) =>
        qc.fetchQuery({
          queryKey: keys.setsInGroup(g.slug),
          queryFn: () => api.get(`groups/${g.slug}/sets`).json<LinkSet[]>(),
        }),
      );

      if (mode === 'SETS') {
        setState((s) => ({
          ...s,
          running: false,
          done: true,
          total: groups.length,
          completed: groups.length,
        }));
        return;
      }

      // FULL - every link in every set.
      // Filter out holes left by setsByGroup fetches that threw.
      const allSets = setsByGroup.flat().filter((s): s is LinkSet => !!s);
      const total = allSets.length;
      let completed = 0;
      setState((s) => ({ ...s, total, completed: 0 }));
      const linksBySet = await throttledMap(allSets, 6, async (s) => {
        const links = await qc.fetchQuery({
          queryKey: keys.linksInSet(s.slug),
          queryFn: () => api.get(`sets/${s.slug}/links`).json<Link[]>(),
        });
        completed += 1;
        setState((prev) => ({ ...prev, completed }));
        return links;
      });

      // Warm the SW's icon cache by issuing a fetch for every unique icon
      // URL - the Workbox CacheFirst rule stores the response so subsequent
      // <img src> reads work offline. Deduped, best-effort, no error
      // propagation.
      const iconUrls = new Set<string>();
      for (const links of linksBySet) {
        if (!links) continue;
        for (const l of links) {
          if (l.canonicalUrl?.url) iconUrls.add(l.canonicalUrl.url);
        }
      }
      await throttledMap(Array.from(iconUrls), 8, async (u) => {
        try {
          await fetch(`/api/icon-lookup-img?url=${encodeURIComponent(u)}`, {
            credentials: 'include',
          });
        } catch {
          /* best-effort; individual failures don't fail the sync */
        }
      });

      setState((s) => ({ ...s, running: false, done: true }));
    } catch (err) {
      setState((s) => ({
        ...s,
        running: false,
        done: false,
        error: (err as Error).message || 'Prefetch failed',
      }));
    }
  }

  // Run once per (userId, mode) pair after session + config are loaded.
  // Skipped on metered connections unless the user forces via Sync Now.
  useEffect(() => {
    if (!userId || !dc) return;
    if (ranForUserRef.current === `${userId}:${mode}`) return;
    if (mode === 'OFF') {
      ranForUserRef.current = `${userId}:${mode}`;
      return;
    }
    const saveData = (navigator as { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData) {
      // Respect Data Saver - user can still trigger manually.
      ranForUserRef.current = `${userId}:${mode}`;
      return;
    }
    ranForUserRef.current = `${userId}:${mode}`;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode]);

  return { ...state, run };
}

/**
 * Bounded-concurrency map. Individual items that throw are swallowed (with
 * their slot in the output left undefined) so one bad fetch doesn't abort
 * every subsequent prefetch in the batch.
 */
async function throttledMap<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      try {
        out[i] = await fn(items[i]!);
      } catch {
        // Leave out[i] undefined; caller can tolerate holes.
      }
    }
  });
  await Promise.all(workers);
  return out;
}
